import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash2, GripVertical, Save, Send, Image as ImageIcon,
  Loader2, CircleDot, CheckSquare, Type, Upload, X, Link2, Copy, Hash
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'single', label: 'Einzelauswahl', icon: CircleDot },
  { value: 'multiple', label: 'Mehrfachauswahl', icon: CheckSquare },
  { value: 'multiple_quantity', label: 'Mehrfachauswahl mit Anzahl', icon: Hash },
  { value: 'text', label: 'Freitext', icon: Type },
];

function newQuestion() {
  return {
    id: Math.random().toString(36).slice(2, 10),
    text: '',
    type: 'single',
    image_url: null,
    options: ['', ''],
  };
}

export default function SurveyEditPage() {
  const { surveyId } = useParams();
  const isNew = !surveyId || surveyId === 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([newQuestion()]);
  const [status, setStatus] = useState('draft');
  const [realSurveyId, setRealSurveyId] = useState(surveyId);
  const [showOnDashboard, setShowOnDashboard] = useState(false);
  const getToken = () => localStorage.getItem('shrt_token');
  const fileRefs = useRef({});

  useEffect(() => {
    if (!isNew) fetchSurvey();
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const s = res.data;
      setTitle(s.title);
      setDescription(s.description || '');
      setQuestions(s.questions.length ? s.questions : [newQuestion()]);
      setStatus(s.status);
      setShowOnDashboard(s.show_on_dashboard || false);
      setRealSurveyId(s.id);
    } catch { toast.error('Umfrage nicht gefunden'); navigate('/admin/surveys'); }
    finally { setLoading(false); }
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...q.options];
      opts[oIdx] = value;
      return { ...q, options: opts };
    }));
  };

  const addOption = (qIdx) => {
    setQuestions(prev => prev.map((q, i) => i === qIdx ? { ...q, options: [...q.options, ''] } : q));
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx || q.options.length <= 2) return q;
      return { ...q, options: q.options.filter((_, j) => j !== oIdx) };
    }));
  };

  const addQuestion = () => setQuestions(prev => [...prev, newQuestion()]);

  const removeQuestion = (idx) => {
    if (questions.length <= 1) return;
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const moveQuestion = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= questions.length) return;
    setQuestions(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const uploadImage = async (qIdx, file) => {
    if (!realSurveyId) {
      toast.error('Bitte zuerst speichern');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/surveys/${realSurveyId}/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
      updateQuestion(qIdx, 'image_url', res.data.image_url);
      toast.success('Bild hochgeladen');
    } catch {
      toast.error('Fehler beim Hochladen');
    }
  };

  const saveSurvey = async () => {
    if (!title.trim()) { toast.error('Titel ist Pflichtfeld'); return; }
    const validQs = questions.filter(q => q.text.trim());
    if (validQs.length === 0) { toast.error('Mindestens eine Frage erforderlich'); return; }

    setSaving(true);
    try {
      const payload = { title, description, questions: validQs, show_on_dashboard: showOnDashboard };
      if (isNew && !realSurveyId) {
        const res = await axios.post(`${API_URL}/api/surveys`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setRealSurveyId(res.data.survey.id);
        toast.success('Umfrage erstellt');
        navigate(`/admin/surveys/${res.data.survey.id}/edit`, { replace: true });
      } else {
        await axios.put(`${API_URL}/api/surveys/${realSurveyId}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Umfrage gespeichert');
      }
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const sendSurvey = async () => {
    if (!realSurveyId) { toast.error('Bitte zuerst speichern'); return; }
    if (!window.confirm('Umfrage jetzt an alle aktiven Mitglieder senden?')) return;
    
    setSending(true);
    try {
      await saveSurvey();
      const res = await axios.post(`${API_URL}/api/surveys/${realSurveyId}/send`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(res.data.message);
      setStatus('active');
    } catch { toast.error('Fehler beim Senden'); }
    finally { setSending(false); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/admin/surveys')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Alle Umfragen
          </button>
          <div className="flex items-center gap-2">
            <Button onClick={saveSurvey} disabled={saving} variant="outline" className="rounded-none" data-testid="save-survey-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            {status === 'draft' && (
              <Button onClick={sendSurvey} disabled={sending} className="rounded-none uppercase tracking-wider" data-testid="send-survey-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Senden
              </Button>
            )}
          </div>
        </div>

        {/* Public Link (when active/sent) */}
        {realSurveyId && status !== 'draft' && (
          <div className="bg-card border border-border p-4 mb-6 flex items-center gap-3" data-testid="survey-public-link">
            <Link2 className="text-primary flex-shrink-0" size={18} />
            <div className="min-w-0 flex-1">
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Öffentlicher Link</label>
              <div className="flex items-center gap-2">
                <code className="text-sm text-foreground bg-muted/50 px-2 py-1 truncate block flex-1">{`${window.location.origin}/public/survey/${realSurveyId}`}</code>
                <Button
                  variant="outline" size="sm" className="rounded-none flex-shrink-0"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/survey/${realSurveyId}`); toast.success('Link kopiert!'); }}
                  data-testid="copy-survey-link"
                >
                  <Copy size={14} className="mr-1" /> Kopieren
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Title & Description */}
        <div className="bg-card border border-border p-6 mb-6">
          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Titel *</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z.B. Mitgliederumfrage 2026"
            className="rounded-none text-lg font-semibold mb-4"
            data-testid="survey-title-input"
          />
          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Beschreibung</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kurze Einleitung zur Umfrage..."
            className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[60px] resize-y"
            data-testid="survey-desc-input"
          />
          <label className="flex items-center gap-3 mt-4 cursor-pointer select-none" data-testid="show-on-dashboard-toggle">
            <button
              type="button"
              role="switch"
              aria-checked={showOnDashboard}
              onClick={() => setShowOnDashboard(!showOnDashboard)}
              className={`relative w-10 h-5 rounded-full transition-colors ${showOnDashboard ? 'bg-blue-500' : 'bg-muted-foreground/30'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${showOnDashboard ? 'translate-x-5' : ''}`} />
            </button>
            <span className="text-sm">Auf Startseite anzeigen</span>
          </label>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {questions.map((q, qIdx) => (
            <div key={q.id} className="bg-card border border-border p-5" data-testid={`question-${qIdx}`}>
              <div className="flex items-start gap-3">
                {/* Drag / reorder */}
                <div className="flex flex-col gap-0.5 pt-1">
                  <button onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0} className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Nach oben">
                    <GripVertical size={16} />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Question number & type */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-sm font-bold text-primary">Frage {qIdx + 1}</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={q.type}
                        onChange={(e) => updateQuestion(qIdx, 'type', e.target.value)}
                        className="text-xs bg-background border border-input px-2 py-1 focus:outline-none"
                        data-testid={`question-type-${qIdx}`}
                      >
                        {QUESTION_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {questions.length > 1 && (
                        <button onClick={() => removeQuestion(qIdx)} className="text-red-400 hover:text-red-600" title="Frage löschen">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Question text */}
                  <Input
                    value={q.text}
                    onChange={(e) => updateQuestion(qIdx, 'text', e.target.value)}
                    placeholder="Frage eingeben..."
                    className="rounded-none mb-3"
                    data-testid={`question-text-${qIdx}`}
                  />

                  {/* Image upload */}
                  <div className="mb-3">
                    {q.image_url ? (
                      <div className="relative inline-block">
                        <img src={q.image_url} alt="" className="max-h-40 border border-border" />
                        <button
                          onClick={() => updateQuestion(qIdx, 'image_url', null)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                          title="Bild entfernen"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileRefs.current[qIdx]?.click()}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground border border-dashed border-border px-3 py-2 transition-colors"
                        data-testid={`upload-image-${qIdx}`}
                      >
                        <ImageIcon size={16} /> Bild hinzufügen
                      </button>
                    )}
                    <input
                      ref={(el) => { fileRefs.current[qIdx] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) uploadImage(qIdx, e.target.files[0]);
                      }}
                    />
                  </div>

                  {/* Options (for single/multiple) */}
                  {q.type !== 'text' && (
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <div key={oIdx} className="flex items-center gap-2">
                          {q.type === 'single' ? (
                            <div className="w-4 h-4 rounded-full border-2 border-muted-foreground flex-shrink-0" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-muted-foreground flex-shrink-0" />
                          )}
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                            placeholder={`Option ${oIdx + 1}`}
                            className="rounded-none flex-1"
                            data-testid={`option-${qIdx}-${oIdx}`}
                          />
                          {q.options.length > 2 && (
                            <button onClick={() => removeOption(qIdx, oIdx)} className="text-muted-foreground hover:text-red-500">
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => addOption(qIdx)}
                        className="flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                        data-testid={`add-option-${qIdx}`}
                      >
                        <Plus size={14} /> Option hinzufügen
                      </button>
                    </div>
                  )}

                  {q.type === 'text' && (
                    <div className="bg-muted/30 border border-dashed border-border p-3 text-sm text-muted-foreground">
                      Freitext-Antwort (Teilnehmer können frei schreiben)
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Question */}
        <button
          onClick={addQuestion}
          className="w-full mt-4 p-4 border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
          data-testid="add-question-btn"
        >
          <Plus size={20} /> Frage hinzufügen
        </button>
      </div>
    </Layout>
  );
}
